import {
  addHours,
  cargoSubtype,
  CompanyEntity,
  ContainerEntity,
  differenceInHours,
  firstOrUndefined,
  FreightTypeEntity,
  getNextQuarterHour,
  getObjectId,
  isAfter, isEmptyOrNullOrUndefined,
  PersonEntity,
  Quantity,
  ServiceTypeEntity,
  SiteEntity,
  toTransportCategory,
  transportCategories,
  transportCategory,
  TransportCategory,
  transportType, useGetAllFreightTypesQuery,
  useGetPreferredCompany,
  useGetPreferredFreightType,
  useGetPreferredSite,
  WellEntity
} from "@ots-frontend/ots-data";
import {useForm} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {
  Button,
  Divider,
  Grid,
  Group,
  Modal,
  ModalProps,
  NumberInput,
  SegmentedControl,
  Textarea,
  TextInput
} from "@mantine/core";
import {useCallback, useEffect, useMemo, useState} from "react";
import {DateInput} from "../../date/DateInput/DateInput";
import {UnitOfMeasureSelect} from "../../select/entity/UnitOfMeasureSelect/UnitOfMeasureSelect";
import {FreightTypeSelect} from "../../select/entity/FreightTypeSelect/FreightTypeSelect";
import {ServiceTypeSelect} from "../../select/entity/ServiceTypeSelect/ServiceTypeSelect";
import {SiteSelect} from "../../select/entity/SiteSelect/SiteSelect";
import {WellSelect} from "../../select/entity/WellSelect/WellSelect";
import {AppContextType, useApp} from "../../../providers/RegionProvider/RegionProvider";
import {TruckCompanySelect} from "../../select/entity/CompanySelect/TruckCompanySelect";
import {getTransportCategoryColorRefByType} from "../../../util/transport-category-ui-util";
import {PersonSelect} from "../../select/entity/PersonSelect/PersonSelect";
import {PreferenceAwareContainerSelect} from "../../select/entity/ContainerSelect/PreferenceAwareContainerSelect";

interface DispatchRequestFormProps {
  options?: DispatchRequestFormOptions;
  defaultValues?: Partial<DispatchRequestFormValues>;
  onSubmit?: (values: DispatchRequestFormValues) => void;
  submitting?: boolean;
}

export interface DispatchRequestFormOptions {
  category?: TransportCategory
  categoryDisabled?: boolean
  truckCompanySelectionDisabled?: boolean;
  pickupSiteSelectionDisabled?: boolean;
  pickupWellSelectionDisabled?: boolean;
  pickupContainerSelectionDisabled?: boolean;
  deliverySiteSelectionDisabled?: boolean;
  deliveryWellSelectionDisabled?: boolean;
  deliveryContainerSelectionDisabled?: boolean;
  loadDetailsDisabled?: boolean;
  authorizedByDisabled?: boolean;
  crudeOilFreightTypeDefaultEnabled?: boolean
}

export interface DispatchRequestFormValues {
  scheduledDate: Date;
  scheduledEndDate?: Date;
  acceptTimeLimit?: number;
  truckCompany: CompanyEntity | string;
  quantity: Quantity;
  freightType?: FreightTypeEntity | string;
  serviceType?: ServiceTypeEntity | string;
  pickupSite?: SiteEntity | string;
  pickupWell?: WellEntity | string;
  pickupContainer?: ContainerEntity | string;
  deliverySite?: SiteEntity | string;
  deliveryWell?: WellEntity | string;
  deliveryContainer?: ContainerEntity | string;
  authorizedBy: PersonEntity | string | undefined;
  externalId?: string;
  workOrderNumber?: string;
  comments?: string;
  category?: TransportCategory
}

const getInitialValues = (defaultValues: Partial<DispatchRequestFormValues> | undefined, context: AppContextType) => {
  let completionDeadline: number | undefined = undefined

  const startDate = defaultValues?.scheduledDate ?? getNextQuarterHour()

  if (defaultValues?.scheduledEndDate) {
    if (isAfter(defaultValues?.scheduledEndDate, startDate)) {
      completionDeadline = differenceInHours(defaultValues?.scheduledEndDate, startDate)
    }
  }

  if (!completionDeadline) {
    completionDeadline = context?.dispatchRequestDuration ?? 36
  }

  return {
    scheduledDate: defaultValues?.scheduledDate ?? new Date(),
    completionDeadline: completionDeadline,
    acceptTimeLimit: defaultValues?.acceptTimeLimit,
    truckCompany: getObjectId(defaultValues?.truckCompany),
    quantity: defaultValues?.quantity ? defaultValues.quantity.number : 1,
    quantityUom: defaultValues?.quantity ? defaultValues.quantity.units : "loads",
    freightType: getObjectId(defaultValues?.freightType),
    serviceType: getObjectId(defaultValues?.serviceType),
    pickupSite: getObjectId(defaultValues?.pickupSite),
    pickupWell: getObjectId(defaultValues?.pickupWell),
    pickupContainer: getObjectId(defaultValues?.pickupContainer),
    deliverySite: getObjectId(defaultValues?.deliverySite),
    deliveryWell: getObjectId(defaultValues?.deliveryWell),
    deliveryContainer: getObjectId(defaultValues?.deliveryContainer),
    authorizedBy: getObjectId(defaultValues?.authorizedBy),
    externalId: defaultValues?.externalId,
    workOrderNumber: defaultValues?.workOrderNumber,
    comments: defaultValues?.comments,
  }
}

export const DispatchRequestForm = (props: DispatchRequestFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const appContext = useApp();

  const {t} = useTranslation(["common"]);

  const [category, setCategory] = useState<TransportCategory>(defaultValues?.category ?? options?.category ?? transportCategory.water)

  const form = useForm({
    initialValues: getInitialValues(defaultValues, appContext),
    validate: {}
  });

  const {companyId: preferredCompanyId} = useGetPreferredCompany({
    serviceWork: !isEmptyOrNullOrUndefined(form.values.serviceType),
    siteId: form.values.pickupSite,
    regionId: appContext.regionId,
    category: category
  });

  const {siteId: preferredDeliverySiteId} = useGetPreferredSite({
    serviceWork: !isEmptyOrNullOrUndefined(form.values.serviceType),
    siteId: form.values.pickupSite,
    regionId: appContext.regionId,
    type: transportType.delivery,
    category: category
  });

  const {freightTypeId: preferredFreightTypeId} = useGetPreferredFreightType({
    serviceTypeId: getObjectId(form.values.serviceType),
    regionId: appContext.regionId,
  });

  const {data: freightTypes = []} = useGetAllFreightTypesQuery(appContext.regionId);
  const crudeOilFreightType = useMemo(() => firstOrUndefined(
    freightTypes.filter((freightType) =>
      freightType.cargoSubType === cargoSubtype.oil.code &&
      !freightType.doNotUse &&
      freightType.type.toLowerCase() === "crude oil"
    )
  ), [freightTypes]);

  useEffect(() => {
    const disabled = options?.truckCompanySelectionDisabled;
    if (!disabled && preferredCompanyId && !form.values.truckCompany) {
      form.setFieldValue("truckCompany", preferredCompanyId);
    }
  }, [form, preferredCompanyId, options?.truckCompanySelectionDisabled]);

  useEffect(() => {
    const disabled = options?.deliverySiteSelectionDisabled;
    if (!disabled && preferredDeliverySiteId && !form.values.deliverySite) {
      form.setFieldValue("deliverySite", preferredDeliverySiteId);
    }
  }, [form, preferredDeliverySiteId, options?.deliverySiteSelectionDisabled]);

  useEffect(() => {
    if (preferredFreightTypeId && !form.values.freightType) {
      form.setFieldValue("freightType", preferredFreightTypeId);
    }
  }, [form, preferredFreightTypeId]);

  useEffect(() => {
    if (
      options?.crudeOilFreightTypeDefaultEnabled &&
      category === transportCategory.oil &&
      crudeOilFreightType &&
      !form.values.freightType
    ) {
      form.setFieldValue("freightType", crudeOilFreightType.id);
    }
  }, [
    category,
    crudeOilFreightType,
    form,
    form.values.freightType,
    options?.crudeOilFreightTypeDefaultEnabled
  ]);

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        scheduledDate: values.scheduledDate,
        scheduledEndDate: addHours(values.scheduledDate, values.completionDeadline),
        acceptTimeLimit: values.acceptTimeLimit,
        truckCompany: values.truckCompany!,
        quantity: {
          number: values.quantity!,
          units: values.quantityUom!
        },
        freightType: values.freightType,
        serviceType: values.serviceType,
        pickupSite: values.pickupSite,
        pickupWell: values.pickupWell,
        pickupContainer: values.pickupContainer,
        deliverySite: values.deliverySite,
        deliveryWell: values.deliveryWell,
        deliveryContainer: values.deliveryContainer,
        authorizedBy: values.authorizedBy,
        externalId: values.externalId,
        workOrderNumber: values.workOrderNumber,
        comments: values.comments,
      });
    }
  }, [form, onSubmit]);

  const handleSiteChange = useCallback((
    site: SiteEntity | undefined,
    type: "PICKUP" | "DELIVERY"
  ): void => {
    if (type === "PICKUP") {
      form.setFieldValue("pickupContainer", undefined);
      form.setFieldValue("pickupWell", undefined);
    } else if (type === "DELIVERY") {
      form.setFieldValue("deliveryContainer", undefined);
      form.setFieldValue("deliveryWell", undefined);
    }
  }, [form]);

  /**
   * Handles when a pickup/delivery well changes. Resets the pickup/container to undefined.
   *
   * @param wellId the primary key of the selected well
   * @param type transport type
   */
  const handleWellChange = useCallback((
    well: WellEntity | undefined,
    type: "PICKUP" | "DELIVERY"
  ): void => {
    const selectedPickupSiteId = form.getTransformedValues().pickupSite;
    const selectedDeliverySiteId = form.getTransformedValues().deliverySite;

    if (well && well.siteId) {
      if (type === "PICKUP" && selectedPickupSiteId !== well.siteId) {
        form.setFieldValue("pickupSite", well.siteId);
      } else if (type === "DELIVERY" && selectedDeliverySiteId !== well.siteId) {
        form.setFieldValue("deliverySite", well.siteId);
      }
    }
  }, [form]);

  /**
   * Handles when a pickup/delivery container changes. Sets the pickup/delivery site to selected container's site.
   *
   * @param containerId the primary key of the selected container
   * @param type transport type
   */
  const handleContainerChange = useCallback((container: ContainerEntity | undefined, type: "PICKUP" | "DELIVERY"): void => {
    const selectedPickupSiteId = form.getTransformedValues().pickupSite;
    const selectedDeliverySiteId = form.getTransformedValues().deliverySite;

    if (container && container.siteId) {
      if (type === "PICKUP" && selectedPickupSiteId !== container.siteId) {
        form.setFieldValue("pickupSite", container.siteId);
      } else if (type === "DELIVERY" && selectedDeliverySiteId !== container.siteId) {
        form.setFieldValue("deliverySite", container.siteId);
      }
    }
  }, [form]);

  const handleContainerPickupChange = useCallback((container: ContainerEntity[]) => {
    handleContainerChange(firstOrUndefined(container), 'PICKUP')
  }, [handleContainerChange])

  const handleContainerDeliveryChange = useCallback((container: ContainerEntity[]) => {
    handleContainerChange(firstOrUndefined(container), 'DELIVERY')
  }, [handleContainerChange])

  const containerPickupSelector = useMemo(() => {
    return {
      regionId: appContext.regionId,
      siteId: form.getTransformedValues().pickupSite,
      cargoSubtype: category === transportCategory.oil ? cargoSubtype.oil : undefined,
    }
  }, [appContext.regionId, form, category])

  const containerDeliverySelector = useMemo(() => {
    return {
      regionId: appContext.regionId,
      siteId: form.getTransformedValues().deliverySite,
      cargoSubtype: category === transportCategory.oil ? cargoSubtype.oil : undefined,
    }
  }, [appContext.regionId, form, category])

  const onCategoryChange = useCallback((value: string) => {
    const newCategory = toTransportCategory(value)

    setCategory(newCategory)

    if (newCategory === transportCategory.oil) {
      form.setFieldValue("serviceType", undefined)
    }
  }, [form])

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Grid grow gutter="xs">
        <Grid.Col span={4}>
          <DateInput
            valueFormat="MM/DD/YYYY HH:mm"
            label={t("common.entity.dispatchrequest.form.field.date")}
            placeholder={t<string>("common.entity.dispatchrequest.form.field.date.placeholder")}
            styles={{wrapper: {width: "100%"}}}
            withAsterisk
            required
            {...form.getInputProps("scheduledDate")} />
          <NumberInput
            step={1}
            label={t("common.entity.dispatchrequest.form.field.accepttimelimit")}
            placeholder={t<string>("common.entity.dispatchrequest.form.field.accepttimelimit.placeholder")}
            styles={{wrapper: {width: "100%"}}}
            {...form.getInputProps("acceptTimeLimit")}
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <NumberInput
            step={1}
            label={t("common.entity.dispatchrequest.form.field.completiondeadline", "Completion Deadline")}
            placeholder={t<string>("common.entity.dispatchrequest.form.field.completiondeadline.placeholder", "Completion Deadline")}
            styles={{wrapper: {width: "100%"}}}
            withAsterisk
            required
            {...form.getInputProps("completionDeadline")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <TruckCompanySelect
        disabled={options?.truckCompanySelectionDisabled}
        label={t("common.entity.dispatchrequest.form.field.truckcompany")}
        placeholder={t<string>("common.entity.dispatchrequest.form.field.truckcompany.placeholder")}
        selector={{
          regionId: appContext?.regionId
        }}
        styles={{wrapper: {width: "50%"}}}
        clearable
        withAsterisk
        required
        {...form.getInputProps("truckCompany")} />
      <Divider my="sm" variant="dotted"/>
      <Group grow>
        <NumberInput
          disabled={options?.loadDetailsDisabled}
          label={t("common.entity.dispatchrequest.form.field.quantity")}
          placeholder={t<string>("common.entity.dispatchrequest.form.field.quantity.placeholder")}
          withAsterisk
          required
          {...form.getInputProps("quantity")} />
        <UnitOfMeasureSelect
          disabled={options?.loadDetailsDisabled}
          label={t("common.entity.dispatchrequest.form.field.uom")}
          placeholder={t<string>("common.entity.dispatchrequest.form.field.uom.placeholder")}
          clearable
          withAsterisk
          required
          {...form.getInputProps("quantityUom")} />
      </Group>
      <Divider my="sm" variant="dotted"/>
      <SegmentedControl
        my={"md"}
        value={category.code}
        onChange={onCategoryChange}
        color={getTransportCategoryColorRefByType(category)}
        data={transportCategories.map((c) => {
          return {
            label: t<string>(c.displayValue.id, c.displayValue.defaultMessage),
            value: c.code,
            disabled: options?.categoryDisabled
          }
        })}
      />
      <Grid grow gutter="xs">
        <Grid.Col span={4}>
          <FreightTypeSelect
            disabled={options?.loadDetailsDisabled}
            label={t("common.entity.dispatchrequest.form.field.freighttype")}
            placeholder={t<string>("common.entity.dispatchrequest.form.field.freighttype.placeholder")}
            selector={{
              regionId: appContext.regionId,
              cargoSubtype: category === transportCategory.oil ? cargoSubtype.oil : undefined
            }}
            clearable
            withAsterisk
            required
            {...form.getInputProps("freightType")} />
        </Grid.Col>
        <Grid.Col span={4}>
          {category === transportCategory.water &&
            <ServiceTypeSelect
              disabled={options?.loadDetailsDisabled}
              label={t("common.entity.dispatchrequest.form.field.servicetype")}
              placeholder={t<string>("common.entity.dispatchrequest.form.field.servicetype.placeholder")}
              selector={{
                regionId: appContext.regionId,
              }}
              clearable
              {...form.getInputProps("serviceType")} />
          }
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={4}>
          <SiteSelect
            disabled={options?.pickupSiteSelectionDisabled}
            label={t("common.entity.dispatchrequest.form.field.pickupsite")}
            placeholder={t<string>("common.entity.dispatchrequest.form.field.pickupsite.placeholder")}
            selector={{
              regionId: appContext.regionId,
            }}
            onEntityChange={(e) => handleSiteChange(firstOrUndefined(e), "PICKUP")}
            clearable
            {...form.getInputProps("pickupSite")} />
          <WellSelect
            disabled={options?.pickupWellSelectionDisabled}
            label={t("common.entity.dispatchrequest.form.field.pickupwell")}
            placeholder={t<string>("common.entity.dispatchrequest.form.field.pickupwell.placeholder")}
            selector={{
              regionId: appContext.regionId,
              siteId: form.getTransformedValues().pickupSite
            }}
            onEntityChange={(e) => handleWellChange(firstOrUndefined(e), "PICKUP")}
            clearable
            {...form.getInputProps("pickupWell")} />
          <PreferenceAwareContainerSelect
            disabled={options?.pickupContainerSelectionDisabled}
            label={t("common.entity.dispatchrequest.form.field.pickupcontainer")}
            placeholder={t<string>("common.entity.dispatchrequest.form.field.pickupcontainer.placeholder")}
            onEntityChange={handleContainerPickupChange}
            useAbbreviation={typeof form.getTransformedValues().pickupSite !== "undefined"}
            showCargoSubtype={true}
            regionId={appContext.regionId}
            category={category}
            siteId={form.getTransformedValues().pickupSite}
            containerId={form.getTransformedValues().pickupContainer}
            freightTypeId={form.getTransformedValues().freightType}
            serviceTypeId={form.getTransformedValues().serviceType}
            clearable
            {...form.getInputProps("pickupContainer")} />
        </Grid.Col>
        <Grid.Col span={4}>
          <SiteSelect
            disabled={options?.deliverySiteSelectionDisabled}
            label={t("common.entity.dispatchrequest.form.field.deliverysite")}
            placeholder={t<string>("common.entity.dispatchrequest.form.field.deliverysite.placeholder")}
            selector={{
              regionId: appContext.regionId,
            }}
            onEntityChange={(e) => handleSiteChange(firstOrUndefined(e), "DELIVERY")}
            clearable
            {...form.getInputProps("deliverySite")} />
          <WellSelect
            disabled={options?.deliveryWellSelectionDisabled}
            label={t("common.entity.dispatchrequest.form.field.deliverywell")}
            placeholder={t<string>("common.entity.dispatchrequest.form.field.deliverywell.placeholder")}
            selector={{
              regionId: appContext.regionId,
              siteId: form.getTransformedValues().deliverySite
            }}
            onEntityChange={(e) => handleWellChange(firstOrUndefined(e), "DELIVERY")}
            clearable
            {...form.getInputProps("deliveryWell")} />
          <PreferenceAwareContainerSelect
            disabled={options?.deliveryContainerSelectionDisabled}
            label={t("common.entity.dispatchrequest.form.field.deliverycontainer")}
            placeholder={t<string>("common.entity.dispatchrequest.form.field.deliverycontainer.placeholder")}
            onEntityChange={handleContainerDeliveryChange}
            useAbbreviation={typeof form.getTransformedValues().deliverySite !== "undefined"}
            showCargoSubtype={true}
            regionId={appContext.regionId}
            category={category}
            siteId={form.getTransformedValues().deliverySite}
            containerId={form.getTransformedValues().deliveryContainer}
            freightTypeId={form.getTransformedValues().freightType}
            serviceTypeId={form.getTransformedValues().serviceType}
            clearable
            {...form.getInputProps("deliveryContainer")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <PersonSelect
        disabled={options?.authorizedByDisabled}
        label={t("common.entity.dispatchrequest.form.field.authorizedby", "Authorized By")}
        placeholder={t<string>("common.entity.dispatchrequest.form.field.authorizedby.placeholder", "Authorized By")}
        selector={{
          regionId: appContext.regionId,
        }}
        clearable
        {...form.getInputProps("authorizedBy")} />
      <TextInput
        label={t("common.entity.dispatchrequest.form.field.externalid")}
        placeholder={t<string>("common.entity.dispatchrequest.form.field.externalid.placeholder")}
        {...form.getInputProps("externalId")}
      />
      <TextInput
        label={t("common.entity.dispatchrequest.form.field.workordernumber")}
        placeholder={t<string>("common.entity.dispatchrequest.form.field.workordernumber.placeholder")}
        {...form.getInputProps("workOrderNumber")}
      />
      <Textarea
        label={t("common.entity.dispatchrequest.form.field.comments")}
        placeholder={t<string>("common.entity.dispatchrequest.form.field.comments.placeholder")}
        {...form.getInputProps("comments")} />
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface DispatchRequestFormModalProps extends DispatchRequestFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const DispatchRequestFormModal = (props: DispatchRequestFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <DispatchRequestForm defaultValues={defaultValues} onSubmit={onSubmit} submitting={submitting}/>
    </Modal>
  );
};
